import React from 'react';
import { Mail, Phone, MapPin, Facebook, Twitter, Instagram, Linkedin, ArrowRight } from 'lucide-react';
interface SiteFooterProps {
  onNavigate?: (page: string) => void;
}
export function SiteFooter({
  onNavigate
}: SiteFooterProps) {
  const currentYear = new Date().getFullYear();
  const quickLinks = [{
    label: 'Home',
    page: 'home'
  }, {
    label: 'Ship Now',
    page: 'shipping'
  }, {
    label: 'Track Package',
    page: 'tracking'
  }, {
    label: 'About Us',
    page: 'about'
  }, {
    label: 'Contact',
    page: 'contact'
  }];
  const services = [{
    label: 'USA to Lagos Shipping',
    page: 'usa-to-lagos'
  }, {
    label: 'Interstate Delivery',
    page: 'interstate-delivery'
  }, {
    label: 'Express Shipping',
    page: 'express-shipping'
  }, {
    label: 'Cargo Freight',
    page: 'cargo-freight'
  }];
  return <footer className="bg-secondary-900 text-white" data-id="element-1150">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16" data-id="element-1151">
        {/* Mobile Layout */}
        <div className="lg:hidden" data-id="element-1152">
          {/* Logo & Social Row */}
          <div className="flex items-center justify-between mb-6" data-id="element-1153">
            <button onClick={() => onNavigate?.('home')} className="flex items-center gap-2" data-id="element-1154">
              <img src="https://cdn.magicpatterns.com/uploads/vbPywZMiUoiHS4U2ti4APK/Task_Runner_Logo-removebg-preview.png" alt="TaskRunner" className="h-16 w-auto object-contain brightness-0 invert" data-id="element-1155" />
            </button>
            <div className="flex gap-2" data-id="element-1156">
              {[Facebook, Twitter, Instagram, Linkedin].map((Icon, i) => <a key={i} href="#" className="w-8 h-8 bg-secondary-800 rounded-lg flex items-center justify-center text-gray-400 hover:bg-brand hover:text-white transition-colors" data-id="element-1157">
                  <Icon className="w-4 h-4" data-id="element-1158" />
                </a>)}
            </div>
          </div>

          {/* Quick Contact */}
          <div className="flex flex-col gap-2 mb-6 text-sm" data-id="element-1159">
            <a href="tel:+1234567890" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors" data-id="element-1160">
              <Phone className="w-4 h-4 text-brand-400" data-id="element-1161" />
              +1 (234) 567-890
            </a>
            <a href="mailto:hello@taskrunner.com" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors" data-id="element-1162">
              <Mail className="w-4 h-4 text-brand-400" data-id="element-1163" />
              hello@taskrunner.com
            </a>
          </div>

          {/* Navigation Grid */}
          <div className="grid grid-cols-2 gap-x-4 gap-y-3 mb-6" data-id="element-1164">
            {quickLinks.map(link => <button key={link.label} onClick={() => onNavigate?.(link.page)} className="text-left text-gray-400 hover:text-white text-sm transition-colors py-1" data-id="element-1165">
                {link.label}
              </button>)}
          </div>

          {/* CTA Button */}
          <button onClick={() => onNavigate?.('shipping')} className="w-full flex items-center justify-center gap-2 py-3 bg-brand text-white text-sm font-semibold rounded-xl hover:bg-brand-dark transition-colors" data-id="element-1166">
            Start Shipping
            <ArrowRight className="w-4 h-4" data-id="element-1167" />
          </button>
        </div>

        {/* Desktop Layout */}
        <div className="hidden lg:grid grid-cols-4 gap-8" data-id="element-1168">
          {/* Brand Column */}
          <div data-id="element-1169">
            <button onClick={() => onNavigate?.('home')} className="flex items-center gap-2 mb-4" data-id="element-1170">
              <img src="https://cdn.magicpatterns.com/uploads/vbPywZMiUoiHS4U2ti4APK/Task_Runner_Logo-removebg-preview.png" alt="TaskRunner" className="h-20 w-auto object-contain brightness-0 invert" data-id="element-1171" />
            </button>
            <p className="text-gray-400 text-sm leading-relaxed mb-6" data-id="element-1172">
              Your trusted partner for shipping from USA to Nigeria. Fast,
              reliable, and affordable logistics solutions.
            </p>
            <div className="flex gap-3" data-id="element-1173">
              {[Facebook, Twitter, Instagram, Linkedin].map((Icon, i) => <a key={i} href="#" className="w-9 h-9 bg-secondary-800 rounded-lg flex items-center justify-center text-gray-400 hover:bg-brand hover:text-white transition-colors" data-id="element-1174">
                  <Icon className="w-4 h-4" data-id="element-1175" />
                </a>)}
            </div>
          </div>

          {/* Quick Links */}
          <div data-id="element-1176">
            <h4 className="font-semibold text-white mb-4" data-id="element-1177">Quick Links</h4>
            <ul className="space-y-3" data-id="element-1178">
              {quickLinks.map(link => <li key={link.label} data-id="element-1179">
                  <button onClick={() => onNavigate?.(link.page)} className="text-gray-400 hover:text-white text-sm transition-colors" data-id="element-1180">
                    {link.label}
                  </button>
                </li>)}
            </ul>
          </div>

          {/* Services */}
          <div data-id="element-1181">
            <h4 className="font-semibold text-white mb-4" data-id="element-1182">Services</h4>
            <ul className="space-y-3" data-id="element-1183">
              {services.map(service => <li key={service.label} data-id="element-1184">
                  <button onClick={() => onNavigate?.(service.page)} className="text-gray-400 hover:text-white text-sm transition-colors text-left" data-id="element-1185">
                    {service.label}
                  </button>
                </li>)}
            </ul>
          </div>

          {/* Contact */}
          <div data-id="element-1186">
            <h4 className="font-semibold text-white mb-4" data-id="element-1187">Contact Us</h4>
            <ul className="space-y-4" data-id="element-1188">
              <li className="flex items-start gap-3" data-id="element-1189">
                <MapPin className="w-5 h-5 text-brand-400 flex-shrink-0 mt-0.5" data-id="element-1190" />
                <span className="text-gray-400 text-sm" data-id="element-1191">
                  123 Shipping Lane
                  <br data-id="element-1192" />
                  New York, NY 10001
                </span>
              </li>
              <li className="flex items-center gap-3" data-id="element-1193">
                <Phone className="w-5 h-5 text-brand-400 flex-shrink-0" data-id="element-1194" />
                <a href="tel:+1234567890" className="text-gray-400 text-sm hover:text-white transition-colors" data-id="element-1195">
                  +1 (234) 567-890
                </a>
              </li>
              <li className="flex items-center gap-3" data-id="element-1196">
                <Mail className="w-5 h-5 text-brand-400 flex-shrink-0" data-id="element-1197" />
                <a href="mailto:hello@taskrunner.com" className="text-gray-400 text-sm hover:text-white transition-colors" data-id="element-1198">
                  hello@taskrunner.com
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-secondary-800" data-id="element-1199">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 lg:py-6" data-id="element-1200">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3" data-id="element-1201">
            <p className="text-gray-500 text-xs sm:text-sm text-center sm:text-left" data-id="element-1202">
              © {currentYear} TaskRunner. All rights reserved.
            </p>
            <div className="flex items-center gap-4 sm:gap-6" data-id="element-1203">
              <a href="#" className="text-gray-500 hover:text-gray-300 text-xs sm:text-sm transition-colors" data-id="element-1204">
                Privacy
              </a>
              <a href="#" className="text-gray-500 hover:text-gray-300 text-xs sm:text-sm transition-colors" data-id="element-1205">
                Terms
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>;
}